import { Component, Input, SimpleChanges } from '@angular/core';
import { FormGroup, FormBuilder } from '@angular/forms';
import { HttpService } from '../../services/http/http.service';
import { ToastrService } from 'ngx-toastr';
import { AngularFireAuth } from '@angular/fire/auth';
import { CommunicationService } from '../../services/communication/communication.service';
import { FADE_IN } from '../../util/animations/animation';
import { MatDialog } from '@angular/material/dialog';
import { ConfirmPopupComponent } from '../../shared/popups/confirm-popup/confirm-popup.component';
/**
* CareAlly can send communication email or sms to the particular patient as well as he can log some notes to that patient. If the email/sms is invalid then system disables the email/sms option.
*
* CareAlly can send plain (custom) text message or he can select the predefined templates to send the communication messages. If CareAlly select the template then he can able to preview the template before sending it to the patient.
*
* The CareAlly can send two type of communication messages.
*
* 1.	Secure Email/SMS
*
* When CareAlly sends secure email or sms, the end user (patient) can’t get actual message directly in email/SMS. Instated it provide the link, once patient clicks on the link it redirect him to MyCarePlan, where he need to login. Once he get logged in then he can able to read that communication message.
*
* 2.	Insecure Email/SMS
*
* When CareAlly sends insecure email/SMS it can be directly displayed in the email or sms inbox.
*
* Once the message is sent it shows in the list, if it’s a template then CareAlly can preview that template.
*/

@Component({
  selector: 'app-patient-engagement',
  templateUrl: './patient-engagement.component.html',
  styleUrls: ['./patient-engagement.component.scss'],
  animations: [FADE_IN]
})
export class PatientEngagementComponent {
  @Input() patientId;
  @Input() patientDetails: any = {};
  communicationForm: FormGroup;
  showLoader = false;
  showLoaderForTemplate = false;

  communicationType = [
    {
      name: 'email',
      key: 'EMAIL',
      placeholder: `Enter a secure email`,
      buttonText: 'Send',
      checkbox: 'Secure Email'
    },
    {
      name: 'sms',
      key: 'SMS',
      placeholder: `Enter a secure sms`,
      buttonText: 'Send',
      checkbox: 'Secure Sms'
    },
    {
      name: 'call',
      key: 'LOG_CALL',
      placeholder: 'Add a note about this call',
      buttonText: 'Save',
      checkbox: ''
    }
  ]

  selectedType: any = {};
  selectedTemplate: any = {};
  templates = [];
  templateData: any = {};
  lastCommunicationObject = null;

  constructor(private FB: FormBuilder,
    private toastr: ToastrService,
    public auth: AngularFireAuth,
    private dialog: MatDialog,
    private communicationService: CommunicationService,
    private httpService: HttpService) {
    this.communicationService.getTemplates.subscribe(templates => {
      this.templates = templates.filter(x => x.templateType === 'emailTemplate');
    })
    this.communicationService.getTemplateData.subscribe(templateData => {
      this.templateData = templateData;
    })
  }
  
  ngOnChanges(changes: SimpleChanges) {
    if(changes.patientId&& (changes.patientId.currentValue !== changes.patientId.previousValue))
      this.generateCommunicationForm();
  }

  generateCommunicationForm() {
    this.communicationForm = this.FB.group({
      id: this.patientId,
      role: 'Patient',
      messageMedium: '',
      msg: '',
      secure: true,
      dynamicTemplate: ''
    })
    this.onSelectType(this.getMedium());
  }

  /**
   * This function will check the valid email and phone.
   *
   * If the email is valid then it enable "Email" button else it will check for Phone validation.
   *
   * If Phone is valid then it will enable "Phone" button else it will active "log call" button.
   */
  getMedium() {
    const emailStats = this.patientDetails.emailStats;
    const mobileStats = this.patientDetails.mobileStats;
    if(this.lastCommunicationObject&&this.lastCommunicationObject.messageMedium){
      if(this.lastCommunicationObject.messageMedium === 'EMAIL' && ((emailStats && emailStats.emailValid) || !emailStats))
        return 0
      else if (this.lastCommunicationObject.messageMedium === 'SMS' && ((mobileStats && mobileStats.mobileValid) || !mobileStats))
        return 1;
      else
        return 2;
    }else if (this.patientDetails.email && ((emailStats && emailStats.emailValid) || !emailStats))
      return 0;
    else if (this.patientDetails.phone && ((mobileStats && mobileStats.mobileValid) || !mobileStats))
      return 1;
    else
      return 2;
  }

  onSelectMedium(index){
    this.lastCommunicationObject = null;
    this.onSelectType(index);
  }

  /**
   * This will select the communication type
   * @param index
   */
  onSelectType(index) {
    this.selectedType = this.communicationType[index];
    this.communicationForm.patchValue({ messageMedium: this.selectedType.key, msg: '', dynamicTemplate: '' })
    this.selectedTemplate = {};
    this.onChangeCommunicationType();
  }

  /**
   * After selecting the template or custom message user can able to send the communication message
   */
  onSubmit() {
    if (this.communicationForm.valid) {
      this.confirmInsecureMsg().then((res: any) => {
        if (res || this.selectedType.key === "LOG_CALL") {
          this.showLoader = true;
          const payload = this.communicationForm.value;
          if (this.selectedTemplate.id) {
            this.templateData.msg = payload.msg;
            payload.msg = this.parseTemplate(this.selectedTemplate.htmlText);
          } else {
            delete payload.dynamicTemplate;
          }
          this.httpService.post('message', payload)
            .subscribe((response: any) => {
              this.showLoader = false;
              let message = `call log saved successfully`;
              if (payload.messageMedium === 'EMAIL' || payload.messageMedium === 'SMS')
                message = `${payload.secure ? 'Secure' : ''} ${payload.messageMedium.toLowerCase()} sent successfully`;

              this.toastr.success(message, 'Communication', {
                progressBar: true, timeOut: 3000
              });
              this.generateCommunicationForm();
              this.selectedTemplate = {};
            }, error => {
              this.showLoader = false;
              this.toastr.warning(error, '', {
                progressBar: true, timeOut: 3000
              });
            })
        }
      })
    }
  }

  /**
   * This popup will get open when user try to send the insecure communication
   */
  confirmInsecureMsg() {
    return new Promise(resolve => {
      if (!this.communicationForm.value.secure&&this.communicationForm.value.messageMedium !== 'LOG_CALL') {
        const dialogRef = this.dialog.open(ConfirmPopupComponent, {
          width: '450px',
          data: {
            title: { text: 'Warning!' },
            subTitle: { text: `You are choosing to send the message in clear text. Please ensure that you do not include any patient health information and that you do not violate any HIPAA guidelines.` },
            cancelBtn: { text: 'Cancel' },
            okBtn: { text: 'Ok' }
          }
        });
        dialogRef.afterClosed().subscribe(action => {
          if (!action)
            resolve(false)
          else
            resolve(true)
        });
      } else {
        resolve(true)
      }
    })
  }

  /**
   * This function is used to change the communication type
   */
  onChangeCommunicationType() {
    if (this.selectedType.key !== "LOG_CALL")
      this.selectedType.placeholder = `Enter a ${this.communicationForm.value.secure ? 'secure' : ''} ${this.selectedType.name}`
  }

  /**
   * This function is used to reset the template data and set the new template for communication
   */
  onSelectTemplate() {
    this.communicationService.setTemplateData('custom', {}) // clearing custom data if any
    const templateId = this.communicationForm.value.dynamicTemplate;
    if (templateId) {
      if (this.selectedTemplate.questions && this.selectedTemplate.questions.length)
        this.selectedTemplate.isReadMore = false; // close previously opened template

      setTimeout(x => {
        let medium;
        this.selectedTemplate = this.templates.find(x => x.id === templateId);
        // Set is we need to send the secure communication
        this.communicationForm.patchValue({ secure: this.selectedTemplate.secure !== false })

        // Select the medium from template
        if (this.selectedTemplate && this.selectedTemplate.defaultCommMedium) {
          medium = this.selectedTemplate.defaultCommMedium;
          this.communicationForm.patchValue({ messageMedium: medium, msg: '' })
        } 

        if (medium === 'EMAIL')
          this.selectedType = this.communicationType[0];
        else if (medium === 'SMS')
          this.selectedType = this.communicationType[1];

        if (this.selectedTemplate.questions && this.selectedTemplate.questions.length) {
          this.selectedTemplate.questions.map(x => { x.answer = null }) // clear the previously given custom inputs
          this.selectedTemplate.isReadMore = true;
          this.selectedTemplate.valid = false;
        } else {
          this.selectedTemplate.isReadMore = false;
          this.selectedTemplate.valid = true;
        }
      }, 10)
    } else {
      const index = this.getMedium();
      this.communicationForm.patchValue({ secure: true, messageMedium: this.communicationType[index].key })
      this.selectedType = this.communicationType[index];
      this.selectedTemplate = {};
    }
  }

  /**
   * While sending the template to the communication it will parse the template and inserts the custom data
   * @param template
   */
  parseTemplate(template) {
    let html = template;
    const start = html.search("{{");
    let end;
    if (start !== -1) {
      end = html.search("}}");
      const key = html.substring(start + 2, end);
      const value = this.getValue(this.templateData, key);
      html = html.replace(`{{${key}}}`, value);
      return this.parseTemplate(html);
    }
    return html;
  }

  /**
   * This function is used to get the value from deep object
   * @param object
   * @param key
   */
  getValue(object, key) {
    return key
      .split('.')
      .reduce((o, k) => (typeof o === 'string' ? JSON.parse(o) : o)[k], object);
  }

  /**
   * This function gets called on template expand
   * @param template
   */
  onTemplateExpand(template) {
    template.isReadMore = true;
  }

  /**
   * This function gets called on template collapse
   * @param template
   */
  onTemplateClose(template) {
    template.isReadMore = false;
  }

  /**
   * This is a child function which gets called when the user provides all the custom inputs
   * @param flag This
   */
  templateCustomInputs(flag) {
    this.selectedTemplate.valid = flag;
    this.selectedTemplate.isReadMore = flag;
  }

  isShowLoader(event) {
    this.showLoaderForTemplate = event;
  }

  latestMessage(data){
    this.lastCommunicationObject = data.display;
    this.generateCommunicationForm();
    this.communicationForm.patchValue({ secure: this.lastCommunicationObject.secure})
  }
}
